import { useEffect, useMemo, useState } from 'react';
import { CustomButton } from '../../../components/customButton/CustomButton';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { useGetColumns } from '../hooks/useGetColumns';
import { useGetFilters } from '../hooks/useGetFilters';
import { useGetRows } from '../hooks/useGetRows';
import './conformidadesContent.scss';
import { ConformidadesFilters } from './ConformidadesFilters';
import { GENERAR_CONFORMIDADES } from '../../../data/graphQL/mutations/Conformidades/GenerarConformidades';
import { ENVIAR_CONFORMIDADES } from '../../../data/graphQL/mutations/Conformidades/EnviarConformidades';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { GET_IMPRIMIR_CONFORMIDADES } from '../../../data/graphQL/queries/Conformidades/imprimirConformidades';
import moment from 'moment';
import fileDownload from 'js-file-download';
import toast from 'react-hot-toast';

export const ConformidadesContent = ({ setChildrenAction, allowedWrite }) => {
    const [isRefetchRequired, setIsRefetchRequired] = useState(true);
    const [selectionModel, setSelectionModel] = useState([]);

    const [generarConformidades, { loading: loadingGenerarConformidades }] = useMutation(GENERAR_CONFORMIDADES, {
        fetchPolicy: 'no-cache'
    });

    const [imprimirConformidades, { loading: loadingImprimirConformidades }] = useLazyQuery(GET_IMPRIMIR_CONFORMIDADES, {
        fetchPolicy: 'no-cache'
    });

    const [enviarConformidades, { loading: loadingEnviarConformidades }] = useMutation(ENVIAR_CONFORMIDADES, {
        fetchPolicy: 'no-cache'
    });

    const {
        filters,
        handleChangeFilters,
        delegaciones,
        metodos
    } = useGetFilters();

    const isFutureMonth = useMemo(() => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        const filterYear = Number(filters.anio);
        const filterMonth = Number(filters.mes);

        return (filterYear > currentYear || filterMonth > currentMonth);
    }, [filters.mes, filters.anio]);

    const {
        rows,
        setRows,
        loading,
        totalCount,
        handleSearch,
        countByEtapa
    } = useGetRows(filters, setIsRefetchRequired);

    const columns = useGetColumns({ metodos });

    const exportLoading = false;

    const handleGenerarConformidades = async () => {
        const filteredRows = rows.filter(row => selectionModel.includes(row.idEvaluacion) && !row.idConformidad).map(row => row.idEvaluacion);

        if (!filteredRows.length) {
            toast.error("Asegúrate de seleccionar solo evaluaciones que no tengan conformidad generada.");
            return;
        }

        let response = await generarConformidades({
            variables: {
                input: {
                    anio: filters.anio,
                    mes: filters.mes,
                    evaluaciones: filteredRows
                }
            }
        });

        if (response.data.generarConformidades.success) {
            toast.success(`${filteredRows.length} Conformidades generadas correctamente.`);
        } else {
            toast.error("No fue posible generar las conformidades.");
        }

        handleSearch();
        resetSelection();
    };

    const handleEnviarConformidades = async () => {
        const filteredRows = rows.filter(row => selectionModel.includes(row.idEvaluacion) && !!row.idConformidad && row.puedeEnviarMail && !row.fechaProcesamiento).map(row => parseInt(row.idConformidad, 10));

        if (!filteredRows.length) {
            toast.error("Asegúrate de seleccionar solo evaluaciones que tengan conformidad generada, el paciente disponga de un mail y no estén procesadas.");
            return;
        }

        const response = await enviarConformidades({
            variables: {
                input: {
                    conformidades: filteredRows
                }
            }
        });

        if (response.data.enviarConformidades.success) {
            toast.success(`${filteredRows.length} Conformidades enviadas correctamente.`);
            handleSearch();
        } else {
            toast.error("No fue posible enviar las conformidades.");
        }

        resetSelection();
    }

    const handleImprimirConformidades = async () => {
        const filteredRows = rows.filter(row => selectionModel.includes(row.idEvaluacion) && !!row.idConformidad && !row.fechaProcesamiento).map(row => row.idEvaluacion);

        if (!filteredRows.length) {
            toast.error("Asegúrate de seleccionar solo evaluaciones que tengan conformidad generada y no estén procesadas.");
            return;
        }

        const response = await imprimirConformidades({
            variables: {
                evaluaciones: filteredRows.map(id => ({ idEvaluacion: id, mes: filters.mes, anio: filters.anio }))
            }
        });
        if (response.data.imprimirConformidades.success) {
            const arr = new Uint8Array(response.data.imprimirConformidades.valorEsperado);
            const documento = new Blob([arr], { type: 'application/pdf' });
            const fileName = 'Documentos-' + moment().format("DD/MM/YYYY HH:mm:ss") + '.pdf';
            fileDownload(documento, fileName);
            toast.success(`${filteredRows.length} Documentos generados correctamente.`);
            handleSearch();
        } else {
            toast.error("No fue posible generar el documento.");
        }

        resetSelection();
    }

    const estadoChips = [
        { label: 'Generar', count: countByEtapa?.pendiente ?? 0, id: 'generar' },
        { label: 'Gestionar', count: countByEtapa?.generado ?? 0, id: 'gestionar' },
        { label: 'Procesar', count: countByEtapa?.procesado ?? 0, id: 'procesar' },
    ];

    useEffect(() => {
        setChildrenAction(() => {
            return (
                <>
                    {/* Chips de conteo por estado */}
                    <div className='info-row'>
                        <div className='header-actions-conformidades'>
                            <div className='chip-numero-conformidades'>
                                <p><strong>{`${estadoChips[0].label} ${estadoChips[0].count}`}</strong></p>
                            </div>
                            <div className='chip-numero-conformidades'>
                                <p><strong>{"|"}</strong></p>
                            </div>
                            <div className='chip-numero-conformidades'>
                                <p><strong>{`${estadoChips[1].label} ${estadoChips[1].count}`}</strong></p>
                            </div>
                            <div className='chip-numero-conformidades'>
                                <p><strong>{"|"}</strong></p>
                            </div>
                            <div className='chip-numero-conformidades'>
                                <p><strong>{`${estadoChips[2].label} ${estadoChips[2].count}`}</strong></p>
                            </div>
                        </div>
                    </div>
                    <CustomButton
                        sx={{ marginRight: '10px' }}
                        label='Imprimir'
                        loading={loadingImprimirConformidades}
                        onClick={handleImprimirConformidades}
                        variant='outlined'
                        disabled={!selectionModel.length || isFutureMonth}
                        endIcon={<PrintOutlinedIcon />}
                    >
                        Imprimir
                    </CustomButton>
                    <CustomButton
                        sx={{ marginRight: '10px' }}
                        label='Enviar'
                        loading={loadingEnviarConformidades}
                        onClick={handleEnviarConformidades}
                        variant='outlined'
                        disabled={!allowedWrite || !selectionModel.length || isFutureMonth}
                    >
                        Enviar
                    </CustomButton>
                    <CustomButton
                        label='Generar'
                        loading={loadingGenerarConformidades}
                        onClick={handleGenerarConformidades}
                        variant='contained'
                        disabled={!allowedWrite || !selectionModel.length || isFutureMonth}
                    >
                        Generar
                    </CustomButton>
                </>
            )
        }
        );
    }, [
        setChildrenAction,
        allowedWrite,
        rows,
        countByEtapa,
        exportLoading,
        filters.idMetodo,
        filters.anio,
        filters.mes,
        selectionModel,
        loadingGenerarConformidades,
        loadingImprimirConformidades
    ]);

    const handleSelectionChange = (newSelection) => {
        setSelectionModel(newSelection);
    };

    const resetSelection = () => {
        setSelectionModel([]);
    };

    const handleFilters = (name, value) => {
        handleChangeFilters(name, value);

        if (name != 'txtFiltro') {
            setRows([]);
            resetSelection();
            setIsRefetchRequired(true);
        }
    }

    return (
        <div>
            <ConformidadesFilters
                delegaciones={delegaciones}
                metodos={metodos}
                filters={filters}
                handleChangeFilters={handleFilters}
                handleSearch={handleSearch}
                disableFilters={loadingGenerarConformidades || loadingImprimirConformidades}
            />

            <DataGridGenerico
                columns={columns}
                rows={rows}
                totalCount={totalCount}
                loading={loading}
                toolbar={false}
                localeText={{
                    noRowsLabel: isRefetchRequired ?
                        'Haz click en "Buscar" para aplicar los nuevos filtros' :
                        "Sin filas"
                }}
                checkboxSelection={!isFutureMonth}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                entityId='idEvaluacion'
                isRowSelectable={(params) => (
                    !loadingGenerarConformidades &&
                    !loadingImprimirConformidades &&
                    !params.row?.fechaProcesamiento
                )}
            />
        </div>
    );
}